import {getCountryCallingCode, getCountries} from 'libphonenumber-js'
import type {CountryCode} from 'libphonenumber-js'
import type {Country} from '../../../../forms/Form/components/ConsentExperience/types'
import {getCountryName, sortByLocalizedName} from '../../../../../lib/utils/country-names'

export const DEFAULT_COUNTRY: CountryCode = 'US'

export type CountryOption = {
  code: CountryCode
  name: string
  callingCode: string
}

export type PhoneSubmissionDetails = {
  formattedPhone: string
  detectedCountry?: CountryCode
  displayInput?: string
}

export function buildCountryOptions(allowedCountries?: Country[]): CountryOption[] {
  const allCountries = getCountries()
  const supportedCodes = new Set(allCountries)

  const options =
    allowedCountries && allowedCountries.length > 0
      ? allowedCountries
          .filter(c => supportedCodes.has(c.alpha as CountryCode))
          .map(c => ({
            code: c.alpha as CountryCode,
            name: getCountryName(c.alpha),
            callingCode: getCountryCallingCode(c.alpha as CountryCode),
          }))
      : allCountries.map(code => ({
          code,
          name: getCountryName(code),
          callingCode: getCountryCallingCode(code),
        }))

  return sortByLocalizedName(options, o => o.name)
}

export function getCountryDisplayShort(countryCode: CountryCode): string {
  const callingCode = getCountryCallingCode(countryCode)
  return `${countryCode} +${callingCode}`
}

export function countryMatchesSearch(country: CountryOption, searchQuery: string): boolean {
  if (!searchQuery.trim()) return true

  const normalizedQuery = searchQuery.toLowerCase().trim()

  return (
    country.name.toLowerCase().includes(normalizedQuery) ||
    country.code.toLowerCase().includes(normalizedQuery) ||
    `+${country.callingCode}`.includes(normalizedQuery) ||
    country.callingCode.includes(normalizedQuery)
  )
}

export function filterCountriesBySearch(countries: CountryOption[], searchQuery: string): CountryOption[] {
  return countries.filter(c => countryMatchesSearch(c, searchQuery))
}

export function getPhoneSubmissionDetails(
  rawInput: string,
  countryCode: CountryCode,
  parsePhoneNumber: typeof import('libphonenumber-js').parsePhoneNumberFromString,
): PhoneSubmissionDetails {
  const trimmedInput = rawInput.trim()

  if (!trimmedInput) {
    return {formattedPhone: ''}
  }

  if (trimmedInput.startsWith('+')) {
    const parsedInternationalNumber = parsePhoneNumber(trimmedInput)

    if (parsedInternationalNumber?.isPossible()) {
      return {
        formattedPhone: parsedInternationalNumber.format('E.164'),
        detectedCountry: parsedInternationalNumber.country,
        displayInput: parsedInternationalNumber.formatNational(),
      }
    }
  }

  const digitsOnly = rawInput.replace(/\D/g, '')

  if (!digitsOnly) {
    // Preserve non-empty invalid input so the existing PHONE validation can surface an error.
    return {formattedPhone: trimmedInput}
  }

  const callingCode = getCountryCallingCode(countryCode)
  const fullNumber = `+${callingCode}${digitsOnly}`
  const parsed = parsePhoneNumber(digitsOnly, countryCode)

  return {
    formattedPhone: parsed?.isPossible() ? parsed.format('E.164') : fullNumber,
  }
}

export function formatPhoneForSubmission(
  rawInput: string,
  countryCode: CountryCode,
  parsePhoneNumber: typeof import('libphonenumber-js').parsePhoneNumberFromString,
): string {
  return getPhoneSubmissionDetails(rawInput, countryCode, parsePhoneNumber).formattedPhone
}
